import { DEVICE } from '@/entities/device';
import { Modal } from '@/entities/modal';
import Organization from '@/entities/organization';
import PageContext from '@/entities/page..context';
import { Attributes } from '@/entities/page.attributes';
import { StorageItems, StorageItemSiteInfo } from '@/entities/storage.items';
import { WidgetsInfo } from '@/entities/widget.info';
import { loginService } from '@/services/login.service';
import messageBundleService from '@/services/message.bundle.service';
import organizationService from '@/services/organization.service';
import pageAttributesService from '@/services/page.attributes.service';
import { ssoService } from '@/services/sso.service';
import storageService from '@/services/storage.service';
import { AppState } from '@/store/mainModule/state';
import { RootState } from '@/store/root.state';
import Logger from '@/utils/logger.utils';
import { userService } from '@ol/ol-ui-core';
import { ActionContext, ActionTree } from 'vuex';


class Helper {
  static async resetAll(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    const plId: number = await storageService.getPrivateLabelId();
    const storageItems: StorageItems = await storageService.getStorageItems();
    actionContext.commit('setStorageItems', storageItems);

    const hasSSOToken:boolean = await userService.hasSsoToken(plId);

    if (!hasSSOToken) {
      await ssoService.saveSSOTokenFromLoginContext();
    }

    await Helper.resetMessages(actionContext);
    await Helper.resetPageAttributesInfo(actionContext);
    await Helper.resetUserOrganizationList(actionContext);
  }

  static async resetSiteInfo(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    const storageItemSiteInfo: StorageItemSiteInfo = await storageService.getSiteInfo();
    actionContext.commit('setStorageSiteInfo', storageItemSiteInfo);
  }

  static async resetPageAttributesInfo(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    const pageAttributesMap: Map<number, Attributes> = await pageAttributesService.getFlatteredPageAttributes();
    actionContext.commit('setPageAttributesMap', pageAttributesMap);
  }

  static async resetUserOrganizationList(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    const isLoggedIn: boolean = await loginService.isLoggedIn();
    if (isLoggedIn) {
      let userOrganizationList: Array<Organization> = new Array<Organization>();
      try {
        userOrganizationList = await organizationService.getOrganizationList();
      } catch (e) {
        Logger.info(e.toString());
      }
      actionContext.commit('setUserOrganizationList', userOrganizationList);
    }
  }

  static async resetMessages(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    const messages: Map<string, string> = await messageBundleService.getMessages();
    actionContext.commit('setMessages', messages);
  }

  static async clearAll(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    actionContext.commit('clearAll');
  }

  static async resetLanguageCode(actionContext: ActionContext<AppState, RootState>, languageCode: string): Promise<void> {
    actionContext.commit('setLanguageCode', languageCode);
    await this.resetAll(actionContext);
  }

  static async resetDevice(actionContext: ActionContext<AppState, RootState>, device: Array<DEVICE>): Promise<void> {
    actionContext.commit('setDevice', device);
    await this.resetAll(actionContext);
  }

  static async setWidgetsInfo(actionContext: ActionContext<AppState, RootState>, payload: WidgetsInfo): Promise<void> {
    actionContext.commit('setWidgetsInfo', payload);
  }
  static async setPageContext(actionContext: ActionContext<AppState, RootState>, payload: PageContext): Promise<void> {
    actionContext.commit('setPageContext', payload);
  }
  static async setApiEndpoints(actionContext: ActionContext<AppState, RootState>, payload: any): Promise<void> {
    actionContext.commit('setApiEndpoints', payload);
  }

  static async setMobileView(actionContext: ActionContext<AppState, RootState>, payload: boolean): Promise<void> {
    actionContext.commit('setMobileView', payload);
  }
  static async setTouchScreen(actionContext: ActionContext<AppState, RootState>, payload: boolean): Promise<void> {
    actionContext.commit('setTouchScreen', payload);
  }
  static async setModal(actionContext: ActionContext<AppState, RootState>, payload: Modal): Promise<void> {
    actionContext.commit('setModal', payload);
  }

  static async setLoginJstate(actionContext: ActionContext<AppState, RootState>, loginJstate: string): Promise<void> {
    actionContext.commit('setLoginJstate', loginJstate);
  }
}

export const actions: ActionTree<AppState, RootState> = {
  async resetAll(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    await Helper.resetAll(actionContext);
  },

  async resetSiteInfo(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    await Helper.resetSiteInfo(actionContext);
  },

  async resetPageAttributesInfo(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    await Helper.resetPageAttributesInfo(actionContext);
  },

  async resetUserOrganizationList(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    await Helper.resetUserOrganizationList(actionContext);
  },

  async resetMessages(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    await Helper.resetMessages(actionContext);
  },

  async clearAll(actionContext: ActionContext<AppState, RootState>): Promise<void> {
    await Helper.clearAll(actionContext);
  },

  async resetLanguageCode(actionContext: ActionContext<AppState, RootState>, languageCode: string): Promise<void> {
    await Helper.resetLanguageCode(actionContext, languageCode);
  },
  async resetDevice(actionContext: ActionContext<AppState, RootState>, device: Array<DEVICE>): Promise<void> {
    await Helper.resetDevice(actionContext, device);
  },
  async setPageContext(actionContext: ActionContext<AppState, RootState>, payload: PageContext): Promise<void> {
    await Helper.setPageContext(actionContext, payload);
  },

  async setApiEndpoints(actionContext: ActionContext<AppState, RootState>, payload: any): Promise<void> {
    await Helper.setApiEndpoints(actionContext, payload);
  },

  async setWidgetsInfo(actionContext: ActionContext<AppState, RootState>, payload: WidgetsInfo): Promise<void> {
    await Helper.setWidgetsInfo(actionContext, payload);
  },

  async setMobileView(actionContext: ActionContext<AppState, RootState>, payload: boolean): Promise<void> {
    await Helper.setMobileView(actionContext, payload);
  },
  async setTouchScreen(actionContext: ActionContext<AppState, RootState>, payload: boolean): Promise<void> {
    await Helper.setTouchScreen(actionContext, payload);
  },

  async setModal(actionContext: ActionContext<AppState, RootState>, payload: Modal): Promise<void> {
    await Helper.setModal(actionContext, payload);
  },

  async setLoginJstate(actionContext: ActionContext<AppState, RootState>, payload: string): Promise<void> {
    await Helper.setLoginJstate(actionContext, payload);
  },
};
